import { DateTime } from 'luxon'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { ACCOUNT_PERMISSION } from '../../shared/constants'
import { ReservationWorkflowRequest, ReservationWorkflowRequestType } from '../../shared/types'
import { useHasAccountPermission } from '../account-permissions'
import { useObjectActionMutationVariable, useObjectList } from '../api/object'
import { Button } from '../components/button'
import { DateFormatter } from '../components/date-formatter'
import { Dialog, DialogFooter } from '../components/dialog'
import { LOADING } from '../components/loading'
import { Paper } from '../components/paper'
import { PhotoGallery } from '../components/photo-gallery'
import {
	FIELD_LABELS,
	FIELD_ORDER,
	REQUEST_TYPE_LABEL,
	STATUS_BADGE,
	STATUS_BORDER,
	STATUS_LABEL,
} from './const'

const IMAGE_URL_REG = /\.(png|jpe?g|webp|gif|bmp|heic|heif|svg)(\?.*)?$/i
const AUDIO_URL_REG = /\.(webm|mp4|m4a|ogg|mp3|aac|wav)(\?.*)?$/i
const VEHICLE_PHOTO_KEY = 'car_photos'
const VOICE_NOTES_KEY = 'voice_notes'
const FUEL_HIDDEN_FIELDS = new Set([
	'fuel_level',
	'fuel_percent',
	'fuel_percentage',
	'fuel_gauge_percent',
	'fuel_ok',
])
const FUEL_HIDDEN_INNER_FIELDS = new Set(['percent', 'percentage', 'level', 'value', 'ok'])

const isFuelHiddenField = (field: string) => FUEL_HIDDEN_FIELDS.has(field)

const isFuelHiddenInnerField = (parentField = '', field: string) => {
	if (!FUEL_HIDDEN_INNER_FIELDS.has(field)) return false
	return ['fuel', 'fuel_gauge', 'fuelgauge'].includes(parentField.toLowerCase())
}

const toFieldLabel = (field: string) => FIELD_LABELS[field] ?? field.replaceAll('_', ' ')
const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value)

const isLikelyImageUrl = (value: unknown) =>
	typeof value === 'string' &&
	(value.startsWith('data:image/') ||
		value.includes('/admin/uploads/') ||
		((value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/')) &&
			IMAGE_URL_REG.test(value)))

const asPhotoList = (value: unknown) => {
	if (typeof value === 'string' && isLikelyImageUrl(value)) return [value]
	if (Array.isArray(value) && value.length && value.every(item => isLikelyImageUrl(item))) {
		return value
	}
	return null
}

// Voice note z ATD wizardu má dvě možné podoby:
//   1) Array<{ url, duration_seconds?, created_at? }> — typický payload
//   2) Array<string> nebo string — fallback, kdyby přišel jen URL
type VoiceNoteEntry = {
	url: string
	duration_seconds?: number | null
	created_at?: string | null
}

const isLikelyAudioUrl = (value: unknown): value is string =>
	typeof value === 'string' &&
	(value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/')) &&
	AUDIO_URL_REG.test(value)

const isVoiceNoteObject = (value: unknown): value is VoiceNoteEntry => {
	if (!isObjectRecord(value)) return false
	const url = (value as Record<string, unknown>).url
	return isLikelyAudioUrl(url)
}

const asVoiceNoteList = (value: unknown, key = ''): VoiceNoteEntry[] | null => {
	// Pole položek (objekty nebo holé URL).
	if (Array.isArray(value)) {
		if (!value.length) return null
		const entries: VoiceNoteEntry[] = []
		for (const item of value) {
			if (isVoiceNoteObject(item)) {
				entries.push(item)
			} else if (isLikelyAudioUrl(item)) {
				entries.push({ url: item })
			} else {
				return null
			}
		}
		return entries.length ? entries : null
	}
	// Jediný řetězec se zvukovou URL — taky validní.
	if (isLikelyAudioUrl(value)) return [{ url: value }]
	// Jediný objekt s url.
	if (isVoiceNoteObject(value)) return [value]
	// Fallback: pole označené klíčem voice_notes, i kdyby bylo prázdné, vrátíme null
	// (nemá smysl renderovat prázdný přehrávač).
	void key
	return null
}

const formatDurationSeconds = (secs: number | null | undefined) => {
	if (!Number.isFinite(secs as number) || (secs as number) <= 0) return ''
	const total = Math.round(secs as number)
	const m = Math.floor(total / 60)
	const s = total % 60
	return `${m}:${String(s).padStart(2, '0')}`
}

function VoiceNotePlayer({ entries }: { entries: VoiceNoteEntry[] }) {
	return (
		<div className="space-y-2">
			{entries.map((entry, index) => {
				const duration = formatDurationSeconds(entry.duration_seconds)
				const created = entry.created_at ? formatDateTime(entry.created_at) : null
				return (
					<div
						key={`${entry.url}-${index}`}
						className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-2"
					>
						<span
							className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xxs font-semibold text-slate-600 whitespace-nowrap"
							aria-label={`Hlasová poznámka ${index + 1}`}
							title="Hlasová poznámka"
						>
							<span aria-hidden>🎤</span>
							<span>#{index + 1}</span>
						</span>
						<audio
							controls
							preload="metadata"
							src={entry.url}
							className="h-9 flex-1 min-w-[200px]"
						/>
						{duration ? (
							<span className="text-xxs tabular-nums text-slate-500 whitespace-nowrap">
								{duration}
							</span>
						) : null}
						{created ? (
							<span className="text-xxs text-slate-400 whitespace-nowrap">
								{created}
							</span>
						) : null}
						<a
							href={entry.url}
							target="_blank"
							rel="noreferrer"
							className="text-xxs font-semibold text-blue-600 hover:text-blue-700 whitespace-nowrap"
						>
							Stáhnout
						</a>
					</div>
				)
			})}
		</div>
	)
}

const formatNumber = (value: number) => new Intl.NumberFormat('cs-CZ').format(value)

const isDateKey = (key = '') =>
	key.includes('date') || key.includes('time') || key.endsWith('_at') || key === 'timestamp'

const isAmountKey = (key = '') => {
	return (
		key.includes('amount') ||
		key.includes('price') ||
		key.includes('cena') ||
		key.includes('cost') ||
		key.includes('fee') ||
		key.includes('charge')
	)
}

const isPercentKey = (key = '') => {
	return [
		'fuel_level',
		'fuel_percent',
		'fuel_percentage',
		'fuel_gauge_percent',
		'stav_paliva',
		'percent',
		'percentage',
	].includes(key)
}

const isKmKey = (key = '') => {
	return (
		key === 'km' ||
		key.endsWith('_km') ||
		['diff', 'over', 'difference', 'overlimit_diff', 'overlimit_over'].includes(key)
	)
}

const formatDateTime = (value: string) => {
	const parsed = DateTime.fromISO(value)
	if (!parsed.isValid) return null
	return parsed.toFormat('d.M. HH:mm')
}

const getFormData = (payload: Record<string, unknown>): Record<string, unknown> => {
	if (isObjectRecord(payload?.data)) return payload.data as Record<string, unknown>
	if (isObjectRecord(payload)) return payload
	return {}
}

const formatScalarValue = (value: unknown, key = ''): string => {
	if (value === null || value === undefined) return 'neuvedeno'
	if (typeof value === 'boolean') return value ? 'Ano' : 'Ne'
	if (typeof value === 'number') {
		if (isKmKey(key)) return `${formatNumber(value)} km`
		if (isAmountKey(key)) {
			return `${formatNumber(value)} Kč`
		}
		if (isPercentKey(key)) {
			return `${formatNumber(value)} %`
		}
		return formatNumber(value)
	}
	if (typeof value === 'string') {
		const trimmed = value.trim()
		if (!trimmed.length) return 'neuvedeno'
		if (isDateKey(key)) {
			const formatted = formatDateTime(trimmed)
			if (formatted) return formatted
		}
		const normalized = trimmed.replace(/\s+/g, '').replace(',', '.')
		const parsed = Number(normalized)
		if (Number.isFinite(parsed)) {
			if (isKmKey(key)) return `${formatNumber(parsed)} km`
			if (isAmountKey(key)) {
				return `${formatNumber(parsed)} Kč`
			}
			if (isPercentKey(key)) {
				return `${formatNumber(parsed)} %`
			}
		}
		return trimmed
	}
	return 'neuvedeno'
}

const renderValue = (value: unknown, key = '') => {
	// Audio / hlasové poznámky mají přednost před obecným array/object
	// renderingem, jinak by se URL renderovala jen jako odkaz/text.
	if (key === VOICE_NOTES_KEY || (Array.isArray(value) && value.some(isVoiceNoteObject))) {
		const voiceNotes = asVoiceNoteList(value, key)
		if (voiceNotes) {
			return <VoiceNotePlayer entries={voiceNotes} />
		}
	}

	const photos = asPhotoList(value)
	if (photos) {
		const label =
			key === VEHICLE_PHOTO_KEY ? 'Fotodokumentace vozidla' : toFieldLabel(key || 'foto')
		return <PhotoGallery photos={photos} keyPrefix={key || 'photo'} label={label} />
	}

	if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
		return <div className="text-sm text-slate-800">{formatScalarValue(value, key)}</div>
	}

	if (value === null || value === undefined) {
		return <div className="text-sm text-slate-500">neuvedeno</div>
	}

	if (Array.isArray(value)) {
		if (!value.length) return <div className="text-sm text-slate-500">neuvedeno</div>
		return (
			<div className="space-y-2">
				{value.map((item, index) => (
					<div
						key={`${key}-${index}`}
						className="rounded-lg border border-slate-200 bg-white p-2"
					>
						<p className="mb-1 text-xxs font-semibold text-slate-500">
							Položka {index + 1}
						</p>
						{renderValue(item, key)}
					</div>
				))}
			</div>
		)
	}

	if (isObjectRecord(value)) {
		const entries = Object.entries(value).filter(
			([childKey]) => !isFuelHiddenInnerField(key, childKey),
		)
		if (!entries.length) return <div className="text-sm text-slate-500">neuvedeno</div>

		if (typeof value.ok === 'boolean' && entries.length === 1) {
			return (
				<span
					className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${value.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}
				>
					{value.ok ? 'Ano' : 'Ne'}
				</span>
			)
		}

		return (
			<div className="space-y-2">
				{entries.map(([childKey, childValue]) => (
					<div key={childKey} className="rounded-lg border border-slate-200 bg-white p-2">
						<p className="mb-1 text-xxs font-semibold text-slate-500">
							{toFieldLabel(childKey)}
						</p>
						{renderValue(childValue, childKey)}
					</div>
				))}
			</div>
		)
	}

	return <div className="text-sm text-slate-800">{formatScalarValue(String(value), key)}</div>
}

const requestSortWeight = (request: ReservationWorkflowRequest) => {
	const ts = Date.parse(request.updated_at ?? request.created_at ?? '')
	if (Number.isFinite(ts)) return ts
	const parsedId = Number(request.id)
	return Number.isFinite(parsedId) ? parsedId : 0
}

const getLatestByType = (
	requests: ReservationWorkflowRequest[],
	type: ReservationWorkflowRequestType,
) => {
	const list = requests.filter(item => item.request_type === type)
	if (!list.length) return null
	return list.reduce((best, current) =>
		requestSortWeight(current) > requestSortWeight(best) ? current : best,
	)
}

// Pole, která se v editačním modalu nezobrazují vůbec — buď jsou to
// interní metadata (`account_id`, `vehys_workflow`, `timestamp`), nebo
// fotky / pole URL (`car_photos`, `docs`, `*_photo`), které admin v tomto
// modalu editovat nemá.
const HIDDEN_EDIT_FIELDS = new Set([
	'account_id',
	'reservation_id',
	'role',
	'type',
	'timestamp',
	'vehys_workflow',
	'vehys_session',
	'car_photos',
	'docs',
	'odo_photo',
	'fuel_photo',
	'fuel_photos',
	'fuel_gauge_photo',
	'photo',
	'photos',
	'voice_notes',
])

const PHOTO_KEY_REG = /(^|_)(photo|photos|fotky?)$/i

function RequestTypePanel({
	type,
	request,
	isMutating,
	onApprove,
	onEditApprove,
}: {
	type: ReservationWorkflowRequestType
	request: ReservationWorkflowRequest | null
	isMutating: boolean
	onApprove: (id: string) => void
	onEditApprove: (request: ReservationWorkflowRequest) => void
}) {
	if (!request) {
		return (
			<Paper className="rounded-2xl border border-slate-200 p-4">
				<div className="flex items-center justify-between gap-3">
					<h5 className="text-base font-bold text-slate-800">
						{REQUEST_TYPE_LABEL[type]}
					</h5>
					<span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
						Bez záznamu
					</span>
				</div>
				<p className="mt-3 text-sm text-slate-500">
					Zatím nebyl odeslán žádný požadavek pro tuto část.
				</p>
			</Paper>
		)
	}

	const formData = getFormData(request.payload)
	const noteInData =
		typeof formData.note === 'string' && formData.note.trim().length
			? formData.note.trim()
			: null
	const note = noteInData ?? request.note
	const entries = Object.entries(formData)
		.filter(([key]) => key !== 'note' && !isFuelHiddenField(key))
		.sort((a, b) => {
			const aIndex = FIELD_ORDER.indexOf(a[0])
			const bIndex = FIELD_ORDER.indexOf(b[0])
			if (aIndex === -1 && bIndex === -1) return a[0].localeCompare(b[0], 'cs')
			if (aIndex === -1) return 1
			if (bIndex === -1) return -1
			return aIndex - bIndex
		})
	const isPending = request.status === 'pending'

	return (
		<Paper
			className={`rounded-2xl border border-slate-200 border-l-4 p-0 shadow-sm ${STATUS_BORDER[request.status]}`}
		>
			<div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
				<div>
					<div className="flex items-center gap-2">
						<h5 className="text-base font-bold text-slate-800">
							{REQUEST_TYPE_LABEL[type]}
						</h5>
						<span
							className={`rounded-full px-2 py-1 text-xs font-semibold ring-1 ${STATUS_BADGE[request.status]}`}
						>
							{STATUS_LABEL[request.status]}
						</span>
					</div>
					<div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-500">
						<span>
							Čas: <DateFormatter date={request.created_at ?? undefined} />
						</span>
						<span>
							Odeslal: {request.requested_by_role === 'admin' ? 'Admin' : 'User'}
						</span>
					</div>
				</div>

				{isPending ? (
					<div className="flex flex-wrap gap-2">
						<Button
							size="SMALL"
							variant="PRIMARY"
							isDisabled={isMutating}
							onPress={() => onApprove(request.id)}
						>
							Schválit
						</Button>
						<Button
							size="SMALL"
							variant="SECONDARY"
							isDisabled={isMutating}
							onPress={() => onEditApprove(request)}
						>
							Upravit a schválit
						</Button>
					</div>
				) : null}
			</div>

			<div className="space-y-4 p-4">
				{note ? (
					<div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
						<span className="font-semibold">Poznámka:</span> {note}
					</div>
				) : null}

				{request.reason ? (
					<div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
						<span className="font-semibold">Důvod zamítnutí:</span> {request.reason}
					</div>
				) : null}

				{entries.length ? (
					<div className="grid gap-3 md:grid-cols-2">
						{entries.map(([field, value]) => {
							const photos = asPhotoList(value)
							const voiceNotes =
								field === VOICE_NOTES_KEY || isVoiceNoteObject(value)
									? asVoiceNoteList(value, field)
									: null
							const fullWidth = !!photos || !!voiceNotes
							return (
								<Paper
									key={field}
									className={`border-slate-200 bg-slate-50 p-3 ${fullWidth ? 'md:col-span-2' : ''}`}
								>
									<p className="mb-2 text-xs font-semibold text-slate-500">
										{toFieldLabel(field)}
									</p>
									{renderValue(value, field)}
								</Paper>
							)
						})}
					</div>
				) : (
					<div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
						Pro tuto část nejsou vyplněná žádná data.
					</div>
				)}
			</div>
		</Paper>
	)
}

function ReservationWorkflowRequestsContent({
	reservationId,
	title,
}: {
	reservationId: string
	title: string
}) {
	const canConfirmReservation = useHasAccountPermission(ACCOUNT_PERMISSION.CONFIRM_RESERVATION)
	const [requestToEdit, setRequestToEdit] = useState<ReservationWorkflowRequest | null>(null)
	const { data, isLoading } = useObjectList('reservation-workflow-request', {
		reservation_id: reservationId,
		page: 1,
		orderKey: 'id',
		orderDir: 'DESC',
	})
	const { mutate: approve, isPending: isApproving } = useObjectActionMutationVariable(
		'reservation-workflow-request',
		'approve',
		[['api', 'reservation']],
		{
			success: () => 'Požadavek schválen',
		},
	)
	const onApprove = (id: string) => approve({ id })
	const onEditApprove = (request: ReservationWorkflowRequest) => setRequestToEdit(request)
	const onSubmitEditApprove = (overrides: Record<string, unknown>) => {
		if (!requestToEdit) return
		approve(
			{ id: requestToEdit.id, data_overrides: overrides },
			{ onSuccess: () => setRequestToEdit(null) },
		)
	}
	const requests = (data?.list ?? []) as unknown as ReservationWorkflowRequest[]
	const isMutating = isApproving

	const handoverRequest = useMemo(() => getLatestByType(requests, 'handover'), [requests])
	const returnRequest = useMemo(() => getLatestByType(requests, 'return'), [requests])
	const pendingCount = useMemo(
		() => [handoverRequest, returnRequest].filter(item => item?.status === 'pending').length,
		[handoverRequest, returnRequest],
	)

	if (!canConfirmReservation) {
		return (
			<Paper className="mt-4 p-4">
				<p className="text-sm text-slate-600">Pro tuto sekci nemáte oprávnění.</p>
			</Paper>
		)
	}

	return (
		<div className="mt-4 space-y-4">
			<Paper className="overflow-hidden border-slate-200">
				<div className="bg-gradient-to-r from-slate-50 via-white to-blue-50 p-4">
					<div className="flex flex-wrap items-start justify-between gap-3">
						<div>
							<h4 className="text-base font-bold text-slate-800">{title}</h4>
							<p className="mt-1 text-xs text-slate-500">
								Každá rezervace má jednu část pro výdej a jednu pro vratku.
							</p>
						</div>
						{pendingCount ? (
							<span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
								Čeká na rozhodnutí: {pendingCount}
							</span>
						) : null}
					</div>

					<div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
						<div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
							<p className="text-xxs font-semibold text-slate-500">Výdej</p>
							<p className="text-sm font-bold text-slate-800">
								{handoverRequest
									? STATUS_LABEL[handoverRequest.status]
									: 'Bez záznamu'}
							</p>
						</div>
						<div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
							<p className="text-xxs font-semibold text-slate-500">Vratka</p>
							<p className="text-sm font-bold text-slate-800">
								{returnRequest ? STATUS_LABEL[returnRequest.status] : 'Bez záznamu'}
							</p>
						</div>
						<div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
							<p className="text-xxs font-semibold text-slate-500">Rezervace</p>
							<p className="text-sm font-bold text-slate-800">{reservationId}</p>
						</div>
						<div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
							<p className="text-xxs font-semibold text-slate-500">Čeká</p>
							<p className="text-sm font-bold text-amber-700">{pendingCount}</p>
						</div>
					</div>
				</div>
			</Paper>

			{isLoading ? (
				<Paper className="p-6">
					<div className="flex justify-center">{LOADING}</div>
				</Paper>
			) : (
				<div className="grid gap-4 xl:grid-cols-2">
					<RequestTypePanel
						type="handover"
						request={handoverRequest}
						isMutating={isMutating}
						onApprove={onApprove}
						onEditApprove={onEditApprove}
					/>
					<RequestTypePanel
						type="return"
						request={returnRequest}
						isMutating={isMutating}
						onApprove={onApprove}
						onEditApprove={onEditApprove}
					/>
				</div>
			)}

			{requestToEdit ? (
				<EditAndApproveDialog
					request={requestToEdit}
					isPending={isApproving}
					onClose={() => setRequestToEdit(null)}
					onSubmit={onSubmitEditApprove}
				/>
			) : null}
		</div>
	)
}

// ────────────────────────────────────────────────────────────────────────
// Edit-and-approve modal
//
// Admin si může před schválením přepsat libovolné top-level pole, které
// pokladna odeslala. Ukládá se jen diff (změněné klíče) jako
// data_overrides, backend ho pak aplikuje na payload před tím, než
// rezervaci finalizuje, a celý diff zaznamená do activity logu.

function isJsonObject(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function valuesAreEqual(a: unknown, b: unknown): boolean {
	if (a === b) return true
	if (a === null || b === null) return false
	if (typeof a !== typeof b) return false
	if (typeof a === 'object') {
		try {
			return JSON.stringify(a) === JSON.stringify(b)
		} catch {
			return false
		}
	}
	return false
}

type EditableEntry = {
	key: string
	originalValue: unknown
	currentValue: unknown
	error?: string | null
}

function deriveEntries(formData: Record<string, unknown>): EditableEntry[] {
	const entries: EditableEntry[] = []
	for (const [key, value] of Object.entries(formData)) {
		if (HIDDEN_EDIT_FIELDS.has(key)) continue
		if (PHOTO_KEY_REG.test(key)) continue
		// Pole URL fotek (i pod neznámým klíčem) — vůbec nezobrazovat.
		if (asPhotoList(value)) continue
		// Hlasové poznámky pod libovolným klíčem (URL nebo objekty s url).
		// Admin je v modalu editovat nemůže, jen přehraje na detailu.
		if (asVoiceNoteList(value, key)) continue
		entries.push({ key, originalValue: value, currentValue: value })
	}
	// Stable řazení podle FIELD_ORDER, aby modal byl konzistentní.
	entries.sort((a, b) => {
		const aIndex = FIELD_ORDER.indexOf(a.key)
		const bIndex = FIELD_ORDER.indexOf(b.key)
		if (aIndex === -1 && bIndex === -1) return a.key.localeCompare(b.key, 'cs')
		if (aIndex === -1) return 1
		if (bIndex === -1) return -1
		return aIndex - bIndex
	})
	return entries
}

function EditAndApproveDialog({
	request,
	isPending,
	onClose,
	onSubmit,
}: {
	request: ReservationWorkflowRequest
	isPending: boolean
	onClose: () => void
	onSubmit: (overrides: Record<string, unknown>) => void
}) {
	const initialFormData = useMemo(
		() => getFormData(request.payload as Record<string, unknown>),
		[request.payload],
	)
	const [entries, setEntries] = useState<EditableEntry[]>(() => deriveEntries(initialFormData))

	const updateEntry = (index: number, next: Partial<EditableEntry>) => {
		setEntries(prev => prev.map((entry, i) => (i === index ? { ...entry, ...next } : entry)))
	}

	const overrides = useMemo(() => {
		const out: Record<string, unknown> = {}
		for (const entry of entries) {
			if (entry.error) continue
			if (!valuesAreEqual(entry.currentValue, entry.originalValue)) {
				out[entry.key] = entry.currentValue
			}
		}
		return out
	}, [entries])

	const hasErrors = entries.some(entry => !!entry.error)
	const changedCount = Object.keys(overrides).length

	const submit = () => {
		if (hasErrors) return
		onSubmit(overrides)
	}

	return (
		<Dialog
			heading={`Upravit a schválit — ${REQUEST_TYPE_LABEL[request.request_type]} #${request.id}`}
			isOpen
			onOpenChange={open => !open && onClose()}
		>
			<div className="space-y-3 p-4 max-h-[70vh] overflow-y-auto">
				<p className="text-xs text-slate-500">
					Změňte hodnoty podle skutečnosti. Při schválení se přepíšou původní data
					z pokladny — diff je vidět v historii rezervace.
				</p>
				{entries.length === 0 ? (
					<p className="text-sm text-slate-500">Tento požadavek nemá editovatelná pole.</p>
				) : (
					<div className="space-y-3">
						{entries.map((entry, index) => (
							<EditableField
								key={entry.key}
								entry={entry}
								onChange={next => updateEntry(index, next)}
							/>
						))}
					</div>
				)}
			</div>
			<DialogFooter>
				<div className="flex flex-1 items-center text-xs text-slate-500">
					{changedCount > 0
						? `${changedCount} ${changedCount === 1 ? 'změna' : changedCount < 5 ? 'změny' : 'změn'}`
						: 'Bez změn'}
				</div>
				<Button variant="SECONDARY" onPress={onClose}>
					Zrušit
				</Button>
				<Button
					variant="PRIMARY"
					isPending={isPending}
					isDisabled={hasErrors}
					onPress={submit}
				>
					Schválit{changedCount > 0 ? ` (${changedCount})` : ''}
				</Button>
			</DialogFooter>
		</Dialog>
	)
}

// Detekuje wrapper objekty typu `{ ok: bool }` nebo `{ has: bool }`,
// které ve wizardu používáme pro odpovědi „Ano / Ne". Pro modal stačí
// nabídnout přepínač pro tu boolean hodnotu — ostatní vlastnosti
// (např. cash by mohlo mít jen `{ has }`) zůstanou zachovány v
// `currentValue` přes shallow merge, ale nezobrazujeme je samostatně.
function getBooleanFlagKey(value: unknown): 'ok' | 'has' | null {
	if (!isJsonObject(value)) return null
	if (typeof value.ok === 'boolean') return 'ok'
	if (typeof value.has === 'boolean') return 'has'
	return null
}

function EditableField({
	entry,
	onChange,
}: {
	entry: EditableEntry
	onChange: (next: Partial<EditableEntry>) => void
}) {
	const { key, originalValue, currentValue, error } = entry
	const label = toFieldLabel(key)
	const inputClass =
		'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200'
	const labelBlock = (
		<div className="flex items-baseline justify-between gap-2">
			<span className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span>
			{!valuesAreEqual(originalValue, currentValue) ? (
				<span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
					změněno
				</span>
			) : null}
		</div>
	)

	// 1) Wrapper objekty s boolean příznakem ({ok}, {has}) → Ano/Ne přepínač.
	//    Sem padne `cash`, `deposit_back`, `fuel`, `gps`, `docs_back`, …
	const flagKey = getBooleanFlagKey(originalValue) ?? getBooleanFlagKey(currentValue)
	if (flagKey) {
		const obj = isJsonObject(currentValue) ? (currentValue as Record<string, unknown>) : {}
		const flagValue = obj[flagKey] === true
		return (
			<label className="flex flex-col gap-1.5">
				{labelBlock}
				<select
					className={inputClass}
					value={String(flagValue)}
					onChange={event =>
						onChange({ currentValue: { ...obj, [flagKey]: event.target.value === 'true' } })
					}
				>
					<option value="true">Ano</option>
					<option value="false">Ne</option>
				</select>
			</label>
		)
	}

	// 2) Holé booleans
	if (typeof originalValue === 'boolean' || typeof currentValue === 'boolean') {
		return (
			<label className="flex flex-col gap-1.5">
				{labelBlock}
				<select
					className={inputClass}
					value={String(currentValue)}
					onChange={event => onChange({ currentValue: event.target.value === 'true' })}
				>
					<option value="true">Ano</option>
					<option value="false">Ne</option>
				</select>
			</label>
		)
	}

	// 3) `{ km: number }` — speciální case pro tachometr.
	if (
		isJsonObject(originalValue) &&
		'km' in originalValue &&
		typeof originalValue.km === 'number'
	) {
		const obj = isJsonObject(currentValue) ? (currentValue as Record<string, unknown>) : {}
		const km = typeof obj.km === 'number' ? obj.km : originalValue.km
		return (
			<label className="flex flex-col gap-1.5">
				{labelBlock}
				<div className="flex items-center gap-2">
					<input
						type="number"
						className={inputClass}
						value={Number.isFinite(km) ? String(km) : ''}
						onChange={event => {
							const raw = event.target.value
							const parsed = raw === '' ? 0 : Number(raw)
							onChange({
								currentValue: { ...obj, km: parsed },
								error: Number.isFinite(parsed) ? null : 'Neplatné číslo',
							})
						}}
					/>
					<span className="text-xs text-slate-500">km</span>
				</div>
				{error ? <span className="text-xs text-rose-600">{error}</span> : null}
			</label>
		)
	}

	// 4) Čísla (částky, overlimit, atd.)
	if (typeof originalValue === 'number') {
		const isAmount = isAmountKey(key)
		return (
			<label className="flex flex-col gap-1.5">
				{labelBlock}
				<div className="flex items-center gap-2">
					<input
						type="number"
						className={inputClass}
						value={Number.isFinite(currentValue as number) ? String(currentValue) : ''}
						onChange={event => {
							const raw = event.target.value
							if (raw.trim() === '') {
								onChange({ currentValue: 0, error: null })
							} else {
								const parsed = Number(raw)
								onChange({
									currentValue: parsed,
									error: Number.isFinite(parsed) ? null : 'Neplatné číslo',
								})
							}
						}}
					/>
					{isAmount ? <span className="text-xs text-slate-500">Kč</span> : null}
				</div>
				{error ? <span className="text-xs text-rose-600">{error}</span> : null}
			</label>
		)
	}

	// 5) Stringy (poznámka apod.)
	if (typeof originalValue === 'string') {
		const isLong = key === 'note' || (typeof currentValue === 'string' && currentValue.length > 60)
		if (isLong) {
			return (
				<label className="flex flex-col gap-1.5">
					{labelBlock}
					<textarea
						rows={3}
						className={inputClass}
						value={String(currentValue ?? '')}
						onChange={event => onChange({ currentValue: event.target.value })}
					/>
				</label>
			)
		}
		return (
			<label className="flex flex-col gap-1.5">
				{labelBlock}
				<input
					type="text"
					className={inputClass}
					value={String(currentValue ?? '')}
					onChange={event => onChange({ currentValue: event.target.value })}
				/>
			</label>
		)
	}

	// 6) Cokoli neznámého — ukázat jen jako readonly informaci. Nechceme,
	//    aby admin musel ručně editovat JSON; pokud je to něco důležitého,
	//    doplní se sem speciální editor v budoucnu.
	const preview = (() => {
		try {
			return JSON.stringify(currentValue, null, 2)
		} catch {
			return String(currentValue)
		}
	})()
	return (
		<div className="flex flex-col gap-1.5">
			{labelBlock}
			<pre className="overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
				{preview}
			</pre>
			<span className="text-[11px] text-slate-400">
				Toto pole se v této verzi nedá editovat — pokud potřebuješ něco změnit, dej vědět.
			</span>
		</div>
	)
}

export function ReservationWorkflowRequestsRoute() {
	const { reservationId = '' } = useParams()
	return (
		<ReservationWorkflowRequestsContent reservationId={reservationId} title="Výdej a vratka" />
	)
}
